import React, { type ReactNode, type FC, type PropsWithChildren } from 'react';

import styled from '@emotion/styled';
import { variant } from '@lucid/theme';

import { type Lucid } from '../../types/types';
import Box from '../box/box';
import Heading, { type HeadingProps } from '../heading/heading';
import VisibleVideo, { type VisibleVideoProps } from '../video/visible-video/visible-video';
import ImageWithPlaceholder, { type ImageWithPlaceholderProps } from '../image/image-with-placeholder';
import Link, { type LinkProps } from '../link/link';
import Text, { type TextProps } from '../text/text';
import decorate from '../decorate/decorate';
import VideoPlayer from '../video/video-player/video-player';

export interface GridHeroSubComponents {
  /** The Heading Subcomponent for GridHero. */
  Heading: FC<PropsWithChildren<HeadingProps>>;
  /** The Subheading Subcomponent for GridHero */
  Subheading: FC<PropsWithChildren<TextProps>>;
  /** The Image Subcomponent for GridHero */
  Image: FC<PropsWithChildren<ImageWithPlaceholderProps>>;
  /** The Link Subcomponent for GridHero */
  Link: FC<PropsWithChildren<LinkProps>>;
  /** The Text Subcomponent for GridHero */
  Text: FC<PropsWithChildren<TextProps>>;
  /** The Video Subcomponent for GridHero */
  Video: FC<PropsWithChildren<VisibleVideoProps>>;
  /** The Buttons container Subcomponent for GridHero */
  Buttons: FC<PropsWithChildren>;
}

export interface GridHeroProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The GridHero component children. */
  children?: ReactNode;
  /** Adjusts text color for dark backgrounds */
  hasDarkBackground?: boolean;
  /** The variants for the GridHero component. */
  variant?: Lucid.Theme.Component.Hero.GridHero.VariantKeys;
}

const StyledGridHero = styled.div<GridHeroProps>(
  variant({
    scale: 'hero.gridHero',
  }),
  {
    label: 'gridHero',
  },
);

const GridHeroComponent: FC<GridHeroProps> & GridHeroSubComponents = ({
  children,
  className,
  variant = 'loweredSub',
  ...rest
}) => (
  <StyledGridHero {...rest} className={className} variant={variant}>
    {children}
  </StyledGridHero>
);

GridHeroComponent.Heading = props => <Heading data-hero-role="heading" {...props} />;
GridHeroComponent.Subheading = props => <Text data-hero-role="subheading" {...props} />;
GridHeroComponent.Image = props => <ImageWithPlaceholder data-hero-role="media" {...props} />;
GridHeroComponent.Link = props => <Link data-hero-role="link" {...props} />;
GridHeroComponent.Text = props => <Text data-hero-role="text" {...props} />;

GridHeroComponent.Video = props => (
  <Box data-hero-role="media">
    {props.src.includes('wistia') ? <VideoPlayer url={props.src} /> : <VisibleVideo {...props} />}
  </Box>
);
GridHeroComponent.Buttons = props => <Box data-hero-role="buttons" {...props} />;
GridHeroComponent.displayName = 'GridHero';
const GridHero = decorate<GridHeroProps, GridHeroSubComponents>(GridHeroComponent);

export { GridHero };
export default GridHero;
